import { z } from "zod/v4";
import { getClientToken, refreshClientToken } from "../auth";

export const ADMIN_API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8080";

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
	) {
		super(message);
		this.name = "ApiError";
	}
}

// `new Headers(init)` accepts a Headers instance, a tuple list, or a record;
// spreading a Headers instance yields {} and silently drops every header.
function authHeaders(init?: HeadersInit): Headers {
	const headers = new Headers(init);
	if (!headers.has("Content-Type")) {
		headers.set("Content-Type", "application/json");
	}
	const token = getClientToken();
	if (token && !headers.has("Authorization")) {
		headers.set("Authorization", `Bearer ${token}`);
	}
	return headers;
}

async function fetchWithRetry(url: string, options?: RequestInit): Promise<Response> {
	const res = await fetch(url, {
		...options,
		headers: authHeaders(options?.headers),
	});

	if (res.status === 401) {
		const newToken = await refreshClientToken();
		if (newToken) {
			return fetch(url, {
				...options,
				headers: authHeaders(options?.headers),
			});
		}
	}

	return res;
}

export async function fetchApi<T>(
	path: string,
	schema: z.ZodType<T>,
	options?: RequestInit,
): Promise<T> {
	const res = await fetchWithRetry(`${ADMIN_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}

	let json: unknown;
	try {
		json = await res.json();
	} catch {
		throw new ApiError(res.status, "Empty or invalid response from server");
	}
	return schema.parse(json);
}

export async function fetchVoid(path: string, options?: RequestInit): Promise<void> {
	const res = await fetchWithRetry(`${ADMIN_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}
}

export async function fetchText(path: string, options?: RequestInit): Promise<string> {
	const res = await fetchWithRetry(`${ADMIN_API_URL}${path}`, options);

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}

	return res.text();
}

type FormDataMethod = "POST" | "PUT";

export async function fetchFormData<T>(
	path: string,
	schema: z.ZodType<T>,
	formData: FormData,
	method: FormDataMethod = "POST",
): Promise<T> {
	const headers: Record<string, string> = {};
	const token = getClientToken();
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}
	const res = await fetch(`${ADMIN_API_URL}${path}`, {
		body: formData,
		headers,
		method,
	});

	if (res.status === 401) {
		const newToken = await refreshClientToken();
		if (newToken) {
			const retryHeaders: Record<string, string> = {};
			retryHeaders.Authorization = `Bearer ${newToken}`;
			const retry = await fetch(`${ADMIN_API_URL}${path}`, {
				body: formData,
				headers: retryHeaders,
				method,
			});
			if (!retry.ok) {
				const body = await retry.text();
				throw new ApiError(retry.status, body);
			}
			let retryJson: unknown;
			try {
				retryJson = await retry.json();
			} catch {
				throw new ApiError(retry.status, "Empty or invalid response from server");
			}
			return schema.parse(retryJson);
		}
	}

	if (!res.ok) {
		const body = await res.text();
		throw new ApiError(res.status, body);
	}

	let json: unknown;
	try {
		json = await res.json();
	} catch {
		throw new ApiError(res.status, "Empty or invalid response from server");
	}
	return schema.parse(json);
}

export function toSearchParams(params: object): string {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue;
		search.set(key, String(value));
	}
	return search.toString();
}

export const paginatedSchema = <T>(itemSchema: z.ZodType<T>) =>
	z.object({
		items: z.array(itemSchema),
		limit: z.number(),
		next_cursor: z.string().optional(),
		offset: z.number(),
		total: z.number(),
	});

export type Paginated<T> = {
	items: T[];
	total: number;
	limit: number;
	offset: number;
	next_cursor?: string;
};
